import isEmpty from '@automator/utilities/is-empty';
import isDefined from '@automator/utilities/is-defined';

/**
 * Does an AJAX call
 *
 * @deprecated Use @wordpress/api-fetch
 *
 * @param {String}   options.url     The URL
 * @param {Object}   options.data    The data sent
 * @param {Callback} onSuccess       The callback invoked if the call is successful
 * @param {Object}   headers         The request headers
 * @param {Boolean}  isWordPressREST TRUE if this is a WordPress REST call
 * @return {FetchData}                 An instance of FetchData
 */
const fetchData = ( { url, data = {}, onSuccess, onFail, headers = {}, isWordPressREST = false } ) => {
	// Create an instance of FetchData and return it
	return new FetchData( {
		url: url,
		data: data,
		onSuccess: onSuccess,
		onFail: onFail,
		headers: headers,
		isWordPressREST: isWordPressREST,
	} );
};

export default fetchData;

export class FetchData {
	constructor( { url, data = {}, onSuccess, onFail, headers = {}, isWordPressREST = false } ) {
		// Check if the url is defined
		if ( ! isEmpty( url ) ) {
			// Save url
			this.url = url;

			// Set data
			this.data = data;

			// Set headers
			this.headers = headers;

			// Set onSuccess callback
			this.onSuccess = onSuccess;

			// Set onFail callback
			this.onFail = onFail;

			// Check if this is a REST call
			this.isWordPressREST = isWordPressREST;

			// Do the request
			this.sendRequest();
		} else {
			console.error( `URL not defined.` );
		}
	}

	/**
	 * Does the request
	 *
	 * @return {undefined}
	 */
	sendRequest() {
		fetch( this.request )
			// Handle errors
			.then( this.handleErrors )
			// Parse data
			.then( ( response ) => response.json() )
			// Handle success calls
			.then( ( response ) => {
				this.onSuccess( response );
			} )
			// Catch errors
			// fetch() will only reject a promise if the user is offline, or some unlikely
			// networking error occurs, such a DNS lookup failure.
			.catch( ( error ) => {
				this.onFail( 'generic', { error: error } );
			} );
	}

	/**
	 * Returns the Request instance, with the Headers and URLSearchParams
	 *
	 * @return {Request} The Request instance for the fetch function
	 */
	get request() {
		return new Request( this.url, {
			method: 'POST',
			headers: this.headers,
			body: this.data,
		} );
	}

	/**
	 * Sets the request data
	 *
	 * @param {Object} data Object with data
	 * @return {undefined}
	 */
	set data( data ) {
		this.requestData = data;
	}

	/**
	 * Returns the request data
	 *
	 * @return {URLSearchParams} The data
	 */
	get data() {
		let requestData = {};

		try {
			// Check if we have to set custom data
			if ( isDefined( this.requestData ) ) {
				// Append object
				requestData = Object.assign( requestData, this.requestData );
			}
		} catch ( error ) {
			console.error( `Data: Couldn't set the custom data` );
			console.error( error );
		}

		// Default data

		// Check if we're doing a rest call
		if ( this.isWordPressREST ) {
			requestData.doing_rest = '1';
		}

		return new URLSearchParams( requestData );
	}

	/**
	 * Returns the callback used to handle errors in the call
	 *
	 * @return {Callback} The function to handle errors
	 */
	get handleErrors() {
		// Get messages for each error
		const frontendMessages = {
			// Access denied (access-denied)
			// 403. Usually caused by an invalid nonce or a server configuration.
			// 👉 Reloading the page might fix the issue.
			accessDenied: {
				title: UncannyAutomatorBackend.i18n.error.request.accessDenied.title,
			},
			// Not found.
			// 404. Thrown when Automator tries to do a request to a non-existing endpoint.
			// 👉 Most probably an issue in Automator. Requires support.
			notFound: {
				title: UncannyAutomatorBackend.i18n.error.request.notFound.title,
			},
			// Request timeout.
			// 408. Caused by a server without enough resources.
			// 👉 Requires the user to increase resources.
			timeout: {
				title: UncannyAutomatorBackend.i18n.error.request.timeout.title,
			},
			// Internal server error
			// 500. Server encountered an unexpected condition. We can't know if it's caused by Automator or another plugin automatically, we need to check the debug data shown below. Most of the time is an issue on Automator.
			// 👉 Requires support.
			serverError: {
				title: UncannyAutomatorBackend.i18n.error.request.serverError.title,
			},
			// Parser error.
			// There is likely a conflict with another plugin that throws a warning, making the returned data invalid.
			// 👉 Most of the time, disabling all plugins but Automator will be enough. It might also be an issue in Automator, so it might require support.
			parserError: {
				title: UncannyAutomatorBackend.i18n.error.request.parserError.title,
			},
		};

		return ( response ) => {
			// Define the error code
			let errorCode = '',
				errorMessage = {};

			if ( ! response.ok ) {
				switch ( response.status ) {
					case 400:
						// Error code
						errorCode = 'bad-request';

						// User message
						errorMessage.title = frontendMessages.badRequest.title;

						break;

					case 403:
						// Error code
						errorCode = 'access-denied';

						// User message
						errorMessage.title = frontendMessages.accessDenied.title;

						break;

					case 404:
						// Error code
						errorCode = 'not-found';

						// User message
						errorMessage.title = frontendMessages.notFound.title;

						break;

					case 408:
						// Error code
						errorCode = 'time-out';

						// User message
						errorMessage.title = frontendMessages.timeOut.title;

						break;

					case 500:
						// Error code
						errorCode = 'server-error';

						// User message
						errorMessage.title = frontendMessages.serverError.title;

						break;
				}
			}

			// Check if there was an error
			if ( ! isEmpty( errorCode ) ) {
				// Invoke the onFail callback
				this.onFail( errorCode, response );

				// Throw the error
				throw Error( `${errorCode} ${errorMessage.title}` );
			}

			return response;
		};
	}

	/**
	 * Saves the onSuccess callback defined
	 *
	 * @param {Function} callback Function invoked when the call is successful
	 * @return {undefined}
	 */
	set onSuccess( callback ) {
		this.requestOnSuccess = callback;
	}

	/**
	 * Returns the callback invoked when the call is successful
	 *
	 * @return {Callback} The function
	 */
	get onSuccess() {
		return ( response ) => {
			// Check if the user defined a custom callback
			if ( isDefined( this.requestOnSuccess ) ) {
				// Invoke the callback
				this.requestOnSuccess( response );
			}
		};
	}

	/**
	 * Saves the onFail callback defined
	 *
	 * @param {Function} callback Function invoked when the call fails
	 * @return {undefined}
	 */
	set onFail( callback ) {
		this.requestOnFail = callback;
	}

	/**
	 * Returns the callback invoked when the call fails
	 *
	 * @return {Callback} The function
	 */
	get onFail() {
		return ( errorCode, response ) => {
			// Check if the user defined a custom callback
			if ( isDefined( this.requestOnFail ) ) {
				// Invoke the callback
				this.requestOnFail( errorCode, response );
			}
		};
	}

	/**
	 * Sets the headers for the call
	 *
	 * @param {Object} headers Object with the headers
	 * @return {undefined}
	 */
	set headers( headers = {} ) {
		this.requestHeaders = headers;
	}

	/**
	 * Returns the headers for the call
	 *
	 * @return {Headers} The headers
	 */
	get headers() {
		// Create headers
		const requestHeaders = new Headers();

		try {
			// Check if the user set custom headers
			if ( isDefined( this.requestHeaders ) ) {
				// Iterate headers
				Object.keys( this.requestHeaders ).forEach( ( headerKey ) => {
					requestHeaders.append( headerKey, this.requestHeaders[headerKey] );
				} );
			}
		} catch ( error ) {
			console.error( `Headers: Couldn't set the custom headers` );
			console.error( error );
		}

		// Check if this is a REST call
		if ( this.isWordPressREST ) {
			// Set nonce
			requestHeaders.set( 'X-WP-Nonce', UncannyAutomatorBackend.rest.nonce );
		}

		// Set content type
		requestHeaders.set( 'Content-Type', 'application/x-www-form-urlencoded' );

		// Set content type
		requestHeaders.set( 'Cache-Control', 'no-cache' );

		return requestHeaders;
	}
}
